import React, { useMemo, useState } from 'react';
import { humanize, isStaff, KIDS_LABEL, planOf, pctOf, fmtPct, fmtInt, toList, useUsers } from './lib';
import {
  BarList, ErrorState, Page, Panel, RefreshButton, Segmented, StatCard, StatGrid, StatSkeletons,
} from './ui';

const PERIODS = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 0, label: 'All time' },
];

const LABELS = {
  age_cluster: { '18-24': '18–24', '25-34': '25–34', '35-44': '35–44', '45-54': '45–54', '55+': '55+' },
  kids_cluster: KIDS_LABEL,
  work_schedule: { predictable: 'Predictable', chaotic: 'Chaotic or variable', shifts: 'Shift work' },
  relationship_type: { married: 'Married', cohabitating: 'Living together' },
  language: { it: 'Italian', en: 'English', es: 'Spanish' },
  focus: { partner: 'Partner', kids: 'Kids', both: 'Both' },
  referral_source: { google: 'Google', instagram: 'Instagram', reddit: 'Reddit', word_of_mouth: 'Word of mouth', other: 'Other' },
  yes_no: { yes: 'Yes', no: 'No', somewhat: 'Somewhat' },
  level: { low: 'Low', medium: 'Medium', high: 'High' },
};

/* Count answers to one question; a multi-select answer counts once per option. */
function distribution(users, field, labels, multi) {
  const counts = {};
  let answered = 0;
  users.forEach((u) => {
    const raw = u[field];
    const values = multi ? toList(raw) : (raw ? [raw] : []);
    if (!values.length) return;
    answered += 1;
    values.forEach((v) => {
      const label = labels?.[v] || humanize(v);
      counts[label] = (counts[label] || 0) + 1;
    });
  });
  return {
    answered,
    items: Object.entries(counts)
      .map(([label, value]) => ({ label, value, share: pctOf(value, answered) }))
      .sort((a, b) => b.value - a.value),
  };
}

function Question({ title, users, field, labels, note }) {
  const { answered, items } = distribution(users, field, labels, note === 'pick several');
  return (
    <Panel
      title={title}
      description={`${fmtInt(answered)} of ${fmtInt(users.length)} answered${note ? ` · ${note}` : ''}`}
    >
      <BarList items={items} empty="No answers yet" />
    </Panel>
  );
}

const GROUPS = {
  demographics: [
    ['Age', 'age_cluster', LABELS.age_cluster],
    ['Kids', 'kids_cluster', LABELS.kids_cluster],
    ['Relationship', 'relationship_type', LABELS.relationship_type],
    ['Work schedule', 'work_schedule', LABELS.work_schedule],
    ['App language', 'language', LABELS.language],
  ],
  goals: [
    ['Why they joined', 'motivation', null, 'pick several'],
    ['What they want to improve', 'improvement_areas', null, 'pick several'],
    ['Main focus', 'focus', LABELS.focus],
  ],
  pain: [
    ['Relationship on autopilot', 'pain_autopilot', LABELS.yes_no],
    ['Low energy at the end of the day', 'pain_energy', LABELS.yes_no],
    ['Feeling disconnected', 'pain_connection', LABELS.yes_no],
    ['Tension points', 'tension_points', null, 'pick several'],
  ],
  mindset: [
    ['Stress', 'stress_level', LABELS.level],
    ['Communication', 'communication_level', LABELS.level],
  ],
  acquisition: [
    ['Where they heard about us', 'referral_source', LABELS.referral_source],
  ],
};

const GROUP_OPTIONS = [
  { value: 'demographics', label: 'Who they are' },
  { value: 'goals', label: 'Goals' },
  { value: 'pain', label: 'Pain points' },
  { value: 'mindset', label: 'Mindset' },
  { value: 'acquisition', label: 'Acquisition' },
];

export default function SegmentsPanel() {
  const { data: users, error, loading, refetch, updatedAt } = useUsers();
  const [days, setDays] = useState(0);
  const [group, setGroup] = useState('demographics');

  const cohort = useMemo(() => {
    const cutoff = days ? Date.now() - days * 86400000 : 0;
    return (users || []).filter((u) => !isStaff(u) && (!cutoff || new Date(u.created_at || 0).getTime() >= cutoff));
  }, [users, days]);
  const onboarded = useMemo(() => cohort.filter((u) => u.onboarding_completed), [cohort]);

  const premium = cohort.filter((u) => planOf(u) === 'premium').length;
  const withKids = onboarded.filter((u) => u.kids_cluster && u.kids_cluster !== 'none').length;
  const budgets = onboarded.map((u) => Number(u.time_budget_minutes)).filter((n) => n > 0);
  const avgBudget = budgets.length ? Math.round(budgets.reduce((a, b) => a + b, 0) / budgets.length) : null;

  // Acquisition is asked to everyone; the rest only to people who finished onboarding.
  const population = group === 'acquisition' ? cohort : onboarded;

  return (
    <Page
      title="Segments"
      description="Who your customers are, from their onboarding answers. Staff accounts are left out; the period filters by signup date."
      actions={(
        <>
          <Segmented value={days} onChange={setDays} options={PERIODS} />
          <RefreshButton onClick={refetch} loading={loading} updatedAt={updatedAt} />
        </>
      )}
    >
      {error && <ErrorState error={error} onRetry={refetch} />}
      {!users && loading ? (
        <StatSkeletons count={4} />
      ) : (
        <>
          <StatGrid>
            <StatCard label="Customers" value={fmtInt(cohort.length)} hint={`${fmtInt(onboarded.length)} finished onboarding`} />
            <StatCard label="Premium" value={fmtPct(pctOf(premium, cohort.length), 0)} hint={`${fmtInt(premium)} customers`} />
            <StatCard label="Have kids" value={fmtPct(pctOf(withKids, onboarded.length), 0)} hint="Of those onboarded" />
            <StatCard label="Time per day" value={avgBudget ? `${avgBudget} min` : '—'} hint="Average they said they have" />
          </StatGrid>

          <Segmented value={group} onChange={setGroup} options={GROUP_OPTIONS} size="md" />

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {GROUPS[group].map(([title, field, labels, note]) => (
              <Question key={field} title={title} users={population} field={field} labels={labels} note={note} />
            ))}
          </div>
        </>
      )}
    </Page>
  );
}
